import * as React from 'react'
import { SendIntegrationsContext } from './send-integrations-context'
import { Container } from 'typescript-ioc'
import { AppState } from '@halo/stores/app.state'
import { FileUploader, Well } from '@pushly/aqe/lib/components'
import { AppleFilled } from '@ant-design/icons'
import { Form, Input, Switch, Tooltip } from 'antd'
import { IAbstractApnsPrivateKeyConfiguration, SendIntegrationChannel } from '@halo/interfaces/send-integrations'
import { WebApnsConfigurationModel } from '@halo/models/send-integration-configurations/web-apns-configuration.model'
import {
    NativeP12ApnsConfiguration,
    NativeP8ApnsConfiguration,
} from '@halo/models/send-integration-configurations/native-apns-configurations.model'
import { DomainSendIntegrationModel } from '@halo/models/send-integration-configurations/domain-send-integration.model'
import { Radio } from 'antd/es'
import { SendIntegrationPrivateKeyType } from '@halo/enums/send-integration-private-key-type.enum'
import { simpleNotification } from '@halo/_utils/notifications'
import { IFilePickerValue } from '@pushly/aqe/lib/components/file-uploader/interfaces'
import { FEAT_CHANNEL_IOS } from '@halo/constants'
import titleCase from 'title-case'
import { getBitwiseEnumParts } from '@halo/_utils/bitwise-operators.util'
import { UNAuthorizationOptions } from '@halo/enums/un-authorization-options.enum'

interface IApnsSIWProps {
    integrations: DomainSendIntegrationModel
    className?: string
}

interface IApnsSIWState {
    webPassEnabled: boolean
    nativePassEnabled: boolean
    p8Temp?: { team_id?: string; key_id?: string }
    p12Temp?: any
    p8UploadValue?: string | undefined
    p12UploadValue?: string | undefined
}

export class ApnsSendIntegrationsWell extends React.Component<IApnsSIWProps, IApnsSIWState> {
    public static contextType = SendIntegrationsContext
    public context!: React.ContextType<typeof SendIntegrationsContext>
    public optRef: any = {}

    protected appState: AppState

    public constructor(props: IApnsSIWProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.state = {
            webPassEnabled: false,
            nativePassEnabled: false,
        }
    }

    public componentDidMount() {
        if (this.nativeType.type === SendIntegrationPrivateKeyType.P12) {
            this.setState({ p12UploadValue: this.nativeType.config.getPrivateKeyFileUrl() })
        } else {
            this.setState({ p8UploadValue: this.nativeType.config.getPrivateKeyFileUrl() })
        }
    }

    public render() {
        const { domain } = this.context
        const { integrations } = this.props
        const nativeIOSFlag = this.appState.flags.findActive(FEAT_CHANNEL_IOS)?.getKey()
        const domainHasNativeIOS = domain.flags.includes(nativeIOSFlag!)

        const webApnsConfig = integrations.getWebApnsConfiguration()
        const nativeApnsConfig = integrations.getNativeApnsConfiguration()
        const webEnabled = webApnsConfig.getIsActive()
        const nativeEnabled = nativeApnsConfig.getIsActive()

        return (
            <Well
                className={this.prefixClassName('form')}
                title={
                    <span>
                        <AppleFilled style={{ marginRight: '6px' }} />
                        <span>Apple</span>
                    </span>
                }
                hideFooter={true}
            >
                <div className={this.prefixClassName('web-config-form')}>
                    <div className={this.prefixClassName('web-config-switch')}>
                        <Switch
                            size="small"
                            onChange={(checked, _ev) =>
                                this.handleConfigEnabledChange(checked, SendIntegrationChannel.WEB)
                            }
                            defaultChecked={webEnabled}
                        />
                        <span>WEB</span>
                    </div>
                    {webEnabled && this.renderWebEditor()}
                </div>

                {domainHasNativeIOS && (
                    <div className={this.prefixClassName('web-config-form')}>
                        <div className={this.prefixClassName('web-config-switch')}>
                            <Switch
                                size="small"
                                onChange={(checked, _ev) =>
                                    this.handleConfigEnabledChange(checked, SendIntegrationChannel.NATIVE)
                                }
                                defaultChecked={nativeEnabled}
                            />
                            <span>NATIVE</span>
                        </div>
                        {nativeEnabled && this.renderNativeEditor()}
                    </div>
                )}
            </Well>
        )
    }

    protected get nativeType() {
        const nativeApnsConfig = this.props.integrations.getNativeApnsConfiguration().clone()
        return { type: nativeApnsConfig.getPrivateKeyType(), config: nativeApnsConfig }
    }

    protected handleConfigEnabledChange = (state: boolean, channel: SendIntegrationChannel) => {
        const integrations = this.context.sendIntegrations.clone()
        let config: WebApnsConfigurationModel | NativeP12ApnsConfiguration | NativeP8ApnsConfiguration
        if (channel === SendIntegrationChannel.WEB) {
            config = integrations.getWebApnsConfiguration().clone()
            config.setIsActive(state)
            integrations.setWebApnsConfiguration(config)
        }

        if (channel === SendIntegrationChannel.NATIVE) {
            config = integrations.getNativeApnsConfiguration().clone()
            config.setIsActive(state)
            integrations.setNativeApnsConfiguration(config)
        }

        this.context.setSendIntegrations(integrations)
    }

    protected handleAppBundleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const integrations = this.context.sendIntegrations.clone()
        const config = integrations.getNativeApnsConfiguration().clone()
        const change = ev.target.value

        if (change.length > 3) {
            change.trim()

            config.setAppBundleId(change)
        }
        integrations.setNativeApnsConfiguration(config)
        this.context.setSendIntegrations(integrations, true)
    }

    protected handlePassPhraseChange = (
        ev: React.ChangeEvent<HTMLInputElement> | any,
        channel: SendIntegrationChannel,
    ) => {
        const integrations = this.context.sendIntegrations.clone()

        if (channel === SendIntegrationChannel.WEB) {
            const config = integrations.getWebApnsConfiguration().clone()
            const pack: Partial<IAbstractApnsPrivateKeyConfiguration> = config.getPrivateKey()
            const change = ev.target.value.trim()
            if (change.length > 3) {
                config.setPrivateKey({
                    ...pack,
                    private_key_pass_phrase: change,
                })

                integrations.setWebApnsConfiguration(config)
            }
        }

        if (channel === SendIntegrationChannel.NATIVE) {
            const config = integrations.getNativeApnsConfiguration().clone() as NativeP12ApnsConfiguration
            const change = ev.target.value.trim()
            if (change.length > 3) {
                config.setPrivateKeyPassPhrase(change)

                integrations.setNativeApnsConfiguration(config)
            }
        }
        this.context.setSendIntegrations(integrations)
    }

    protected handleFileUploadError = (res: any) => {
        const error = res?.error

        if (error && error.error_type === 'invalid_file_type_error') {
            return simpleNotification('error', error.message)
        }
    }

    protected handlePrivateKeyChange = async (
        upload: IFilePickerValue | undefined,
        channel: SendIntegrationChannel,
    ) => {
        const integrations = this.context.sendIntegrations.clone()
        const updateValue = upload?.url ?? undefined

        if (channel === SendIntegrationChannel.WEB) {
            const config = integrations.getWebApnsConfiguration().clone()
            const pack: Partial<IAbstractApnsPrivateKeyConfiguration> = {
                ...config.getPrivateKey(),
                private_key_file_url: updateValue,
            }
            config.setPrivateKey(pack)
            integrations.setWebApnsConfiguration(config)
        }

        if (channel === SendIntegrationChannel.NATIVE) {
            const config = integrations.getNativeApnsConfiguration().clone()
            const key = this.nativeType.type === SendIntegrationPrivateKeyType.P8 ? 'p8UploadValue' : 'p12UploadValue'
            await this.setState((state) => ({ ...state, [key]: updateValue }))

            config.setPrivateKeyFileUrl(updateValue)
            integrations.setNativeApnsConfiguration(config)
        }

        this.context.setSendIntegrations(integrations)
    }

    protected handleWebsitePushIdFieldChange = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const integrations = this.context.sendIntegrations.clone()
        const config = integrations.getWebApnsConfiguration().clone()
        const change = ev.target.value

        if (change.length > 3) {
            const pack = config.getPushPackage()
            config.setPushPackage({
                ...pack,
                website_push_id: change,
            })

            integrations.setWebApnsConfiguration(config)
            this.context.setSendIntegrations(integrations, true)
        }
    }

    protected handleP8InputFieldChange = async (
        ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        fieldProp: 'team_id' | 'key_id',
    ) => {
        const integrations = this.context.sendIntegrations.clone()
        const config: NativeP8ApnsConfiguration = integrations
            .getNativeApnsConfiguration()
            .clone() as NativeP8ApnsConfiguration
        const change = ev.target.value

        if (change.length > 3) {
            switch (fieldProp) {
                case 'team_id':
                    config.setTeamId(change)
                    this.setState({
                        p8Temp: {
                            ...this.state.p8Temp,
                            [fieldProp]: change,
                        },
                    })
                    break
                case 'key_id':
                    config.setKeyId(change)
                    this.setState({
                        p8Temp: {
                            ...this.state.p8Temp,
                            [fieldProp]: change,
                        },
                    })
                    break
            }
        }

        integrations.setNativeApnsConfiguration(config)
        this.context.setSendIntegrations(integrations)
    }

    protected handlePermissionsUpdate = async (
        config: NativeP8ApnsConfiguration | NativeP12ApnsConfiguration,
        value: number,
        enabled: boolean,
    ) => {
        config = config.clone()
        const currValue = config.getAdditionalPermissions()
        const newValue = enabled ? currValue + value : currValue - value

        const integrations = this.context.sendIntegrations.clone()
        config.setAdditionalPermissions(newValue)

        integrations.setNativeApnsConfiguration(config)
        this.context.setSendIntegrations(integrations)
    }

    protected renderWebEditor() {
        const config = this.context.sendIntegrations.getWebApnsConfiguration()

        return (
            <>
                <Form.Item label="Website Push ID">
                    <Input
                        size="small"
                        defaultValue={config.getPushPackage()?.website_push_id}
                        onChange={this.handleWebsitePushIdFieldChange}
                    />
                </Form.Item>

                <Form.Item label="Certificate" className={this.prefixClassName('web-certificate-upload')}>
                    <FileUploader
                        className="credential-upload"
                        type="file"
                        private={true}
                        allowedMimeTypes={[...FileUploader.defaultFileTypes.pkcs12]}
                        ownerType="domain"
                        ownerId={this.context.domain.id}
                        buttonText="Upload your APNS private key file (.p12) certificate"
                        buttonIcon={null}
                        defaultValue={
                            config.getPrivateKey().private_key_file_url
                                ? { url: config.getPrivateKey().private_key_file_url! }
                                : undefined
                        }
                        onSuccess={(upload) => this.handlePrivateKeyChange(upload, SendIntegrationChannel.WEB)}
                        onRemove={(upload) => this.handlePrivateKeyChange(upload, SendIntegrationChannel.WEB)}
                        onError={this.handleFileUploadError}
                    />
                </Form.Item>

                <Form.Item label="Pass Phrase" className={this.prefixClassName('p12-cert-pass-phrase')}>
                    <div className={this.prefixClassName('p12-cert-pass-phrase-switch')}>
                        <Switch
                            size="small"
                            onChange={(checked, _) => {
                                config.setPrivateKey({ ...config.getPrivateKey(), private_key_pass_phrase: undefined })
                                this.setState({ webPassEnabled: checked })
                            }}
                            defaultChecked={this.state.webPassEnabled}
                        />
                        <Input
                            size="small"
                            type="password"
                            disabled={!this.state.webPassEnabled}
                            placeholder={this.state.webPassEnabled ? '' : 'No pass phrase'}
                            onChange={(ev) => this.handlePassPhraseChange(ev, SendIntegrationChannel.WEB)}
                            value={config.getPrivateKey()?.private_key_pass_phrase}
                        />
                    </div>
                </Form.Item>
            </>
        )
    }

    protected renderNativeEditor() {
        const config = this.context.sendIntegrations.getNativeApnsConfiguration()
        const allowedFileType =
            config instanceof NativeP8ApnsConfiguration
                ? FileUploader.defaultFileTypes.pkcs8
                : FileUploader.defaultFileTypes.pkcs12
        // @ts-ignore -- indexing state with string literal
        const fileUploaderValue =
            config.getPrivateKeyFileUrl() ||
            this.state?.[`${this.nativeType!.type!.toLowerCase()}UploadValue` as keyof IApnsSIWState]
                ? { url: this.state?.[`${this.nativeType!.type!.toLowerCase()}UploadValue` as keyof IApnsSIWState] }
                : undefined

        const permissionOpts = [
            // tslint:disable-next-line:no-bitwise
            { label: 'Critical Alerts', value: 1 << 4 },
            // tslint:disable-next-line:no-bitwise
            { label: 'Provide App Notification Settings', value: 1 << 5 },
            // tslint:disable-next-line:no-bitwise
            { label: 'Provisional Permissions', value: 1 << 6 },
        ]
        const checkedPermissionOpts = getBitwiseEnumParts(config.getAdditionalPermissions(), UNAuthorizationOptions)

        return (
            <>
                {this.appState.currentUser?.isInternalUser && (
                    <Form.Item label="Additional Permissions">
                        <div className="un-auth-opt-toggles">
                            {permissionOpts.map((opt, idx) => {
                                const optsPerRow = 2
                                const shouldBreak = idx >= optsPerRow && idx % optsPerRow === 0

                                return (
                                    <React.Fragment key={idx}>
                                        {shouldBreak && <div className="break" />}
                                        <label className="auth-opt-toggle">
                                            <Switch
                                                ref={(el) => (this.optRef[UNAuthorizationOptions[opt as any]] = el)}
                                                size="small"
                                                defaultChecked={checkedPermissionOpts.includes(opt.value)}
                                                onChange={(enabled) =>
                                                    this.handlePermissionsUpdate(config, opt.value, enabled)
                                                }
                                            />
                                            <Tooltip title={titleCase(opt.label)}>
                                                <span>{titleCase(opt.label)}</span>
                                            </Tooltip>
                                        </label>
                                    </React.Fragment>
                                )
                            })}
                        </div>
                    </Form.Item>
                )}

                <Radio.Group
                    size="small"
                    buttonStyle="solid"
                    value={config.getPrivateKeyType()}
                    onChange={(ev) => {
                        const integrations = this.context.sendIntegrations.clone()
                        const configUpdate = integrations.getNativeApnsConfiguration().clone()
                        const change = ev.target.value

                        if (change !== config.getPrivateKeyType()) {
                            configUpdate.setPrivateKeyType(change)
                            // clear file uploader to prevent mixed cert types
                            // @ts-ignore -- indexing state with string literal
                            configUpdate.setPrivateKeyFileUrl(this.state?.[`${change.toLowerCase()}UploadValue`])
                        }

                        integrations.setNativeApnsConfiguration(configUpdate)
                        this.context.setSendIntegrations(integrations)
                    }}
                >
                    <Radio.Button value={SendIntegrationPrivateKeyType.P8}>P8 Cert</Radio.Button>
                    <Radio.Button value={SendIntegrationPrivateKeyType.P12}>P12 Cert</Radio.Button>
                </Radio.Group>

                <Form.Item label="Certificate" className={this.prefixClassName('native-certificate-upload')}>
                    <FileUploader
                        key={config.getPrivateKeyType()}
                        className="credential-upload"
                        type="file"
                        private={true}
                        allowedMimeTypes={allowedFileType}
                        ownerType="domain"
                        ownerId={this.context.domain.id}
                        buttonText={`Upload your APNS private key file (.${config
                            .getPrivateKeyType()
                            ?.toLowerCase()}) certificate`}
                        buttonIcon={null}
                        onSuccess={(upload) => this.handlePrivateKeyChange(upload, SendIntegrationChannel.NATIVE)}
                        onRemove={(upload) => this.handlePrivateKeyChange(upload, SendIntegrationChannel.NATIVE)}
                        onError={this.handleFileUploadError}
                        defaultValue={fileUploaderValue}
                        value={fileUploaderValue}
                    />
                </Form.Item>

                {config instanceof NativeP12ApnsConfiguration && (
                    <>
                        <Form.Item label="Pass Phrase" className={this.prefixClassName('p12-cert-pass-phrase')}>
                            <div className={this.prefixClassName('p12-cert-pass-phrase-switch')}>
                                <Switch
                                    size="small"
                                    onChange={(checked, _) => {
                                        config.setPrivateKeyPassPhrase(undefined)
                                        this.setState({ nativePassEnabled: checked })
                                    }}
                                    defaultChecked={this.state.nativePassEnabled}
                                />
                                <Input
                                    size="small"
                                    type="password"
                                    disabled={!this.state.nativePassEnabled}
                                    placeholder={this.state.nativePassEnabled ? '' : 'No pass phrase'}
                                    onChange={(ev) => this.handlePassPhraseChange(ev, SendIntegrationChannel.NATIVE)}
                                    value={config.getPrivateKeyPassPhrase()}
                                />
                            </div>
                        </Form.Item>

                        <Form.Item label="App Bundle ID">
                            <Input
                                size="small"
                                defaultValue={
                                    this.context.domain.native_apns_configuration.app_bundle_id ??
                                    config.getAppBundleId()
                                }
                                onChange={this.handleAppBundleChange}
                            />
                        </Form.Item>
                    </>
                )}

                {config instanceof NativeP8ApnsConfiguration && (
                    <>
                        <Form.Item label="Team ID">
                            <Input
                                size="small"
                                defaultValue={
                                    this.context.domain.native_apns_configuration.team_id ?? config.getTeamId()
                                }
                                onChange={(ev) => this.handleP8InputFieldChange(ev, 'team_id')}
                                value={this.state?.p8Temp?.team_id}
                            />
                        </Form.Item>
                        <Form.Item label="Key ID">
                            <Input
                                size="small"
                                defaultValue={this.context.domain.native_apns_configuration.key_id ?? config.getKeyId()}
                                onChange={(ev) => this.handleP8InputFieldChange(ev, 'key_id')}
                                value={this.state?.p8Temp?.key_id ?? config.getKeyId()}
                            />
                        </Form.Item>
                    </>
                )}
            </>
        )
    }

    protected prefixClassName = (classname: string) => {
        return `${this.props.className}-${classname}`
    }
}
